import { forwardRef, useEffect, useImperativeHandle, useRef } from "react"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import type { SceneThemeId } from "@/lib/location-scene-3d/scene-state"
import {
  ViewCube,
  type ViewCubeHandlers,
  type ViewCubeLabels,
  type ViewCubeVec3,
  type ViewCubeViewId,
} from "@/lib/location-scene-3d/view-cube"
import { cn } from "@/lib/utils"

/** 10px from the top-right of the scene zone. */
export const VIEW_CUBE_CORNER_CLASS = "pointer-events-none absolute top-2.5 right-[14px] z-30"

const ISO_ICON_PATHS = [
  "M3 7V4h3M18 4h3v3M21 17v3h-3M6 20H3v-3",
  "M12 6.5l5 2.8v5.6l-5 2.8-5-2.8V9.3z",
  "M7 9.3l5 2.8 5-2.8M12 12.1v5.6",
]

export type ViewCubeWidgetHandle = {
  setOrientation: (direction: ViewCubeVec3, up: ViewCubeVec3) => void
  setActiveView: (viewId: ViewCubeViewId | null) => void
}

type ViewCubeWidgetProps = ViewCubeHandlers & {
  labels: ViewCubeLabels
  isoLabel: string
  theme: SceneThemeId
  /** Phone: keep the iso button, do not mount the cube. */
  showCube?: boolean
  /** Camera-view mode hides the iso snap. */
  showIso?: boolean
}

export const ViewCubeWidget = forwardRef<ViewCubeWidgetHandle, ViewCubeWidgetProps>(function ViewCubeWidget(
  { labels, isoLabel, theme, showCube = true, showIso = true, onViewSelect, onOrbitStart, onOrbit, onOrbitEnd },
  ref,
) {
  const hostRef = useRef<HTMLDivElement>(null)
  const cubeRef = useRef<ViewCube | null>(null)
  const handlersRef = useRef<ViewCubeHandlers>({})
  const themeRef = useRef(theme)
  themeRef.current = theme
  handlersRef.current = { onViewSelect, onOrbitStart, onOrbit, onOrbitEnd }

  useImperativeHandle(
    ref,
    () => ({
      setOrientation: (direction, up) => cubeRef.current?.setOrientation(direction, up),
      setActiveView: (viewId) => cubeRef.current?.setActiveView(viewId),
    }),
    [],
  )

  useEffect(() => {
    if (!showCube) return
    const host = hostRef.current
    if (!host) return
    const cube = new ViewCube(host, {
      labels,
      theme: themeRef.current,
      activeView: null,
      onViewSelect: (viewId) => handlersRef.current.onViewSelect?.(viewId),
      onOrbitStart: () => handlersRef.current.onOrbitStart?.(),
      onOrbit: (dx, dy) => handlersRef.current.onOrbit?.(dx, dy),
      onOrbitEnd: () => handlersRef.current.onOrbitEnd?.(),
    })
    cubeRef.current = cube
    return () => {
      cube.dispose()
      cubeRef.current = null
    }
  }, [labels, showCube])

  useEffect(() => {
    cubeRef.current?.setSkin(theme)
  }, [theme])

  return (
    <div className="relative">
      {showCube ? <div ref={hostRef} /> : null}
      {showIso ? (
        <ActionHintTooltip label={isoLabel} className="pointer-events-auto absolute top-0 right-0 z-[1] h-auto">
          <button
            type="button"
            aria-label={isoLabel}
            className={cn(
              "flex size-[26px] cursor-pointer items-center justify-center rounded-sm border-0 bg-transparent p-0",
              "transition-colors duration-[0.12s] outline-none",
              theme === "light"
                ? "text-[var(--text-muted)] hover:text-[var(--terra-3)] dark:hover:text-[var(--ui-accent)]"
                : "text-[var(--scene-cube-night-icon)] hover:text-[var(--scene-cube-night-hover-face)] dark:hover:text-[var(--ui-accent)]",
            )}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation()
              if (cubeRef.current) cubeRef.current.selectIso()
              else handlersRef.current.onViewSelect?.("iso")
            }}
          >
            <svg
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              {ISO_ICON_PATHS.map((d) => (
                <path key={d} d={d} />
              ))}
            </svg>
          </button>
        </ActionHintTooltip>
      ) : null}
    </div>
  )
})
